import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FileButton,
  Grid,
  Group,
  Modal,
  MultiSelect,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Stepper,
  Switch,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconCircleCheck,
  IconClock,
  IconDeviceMobile,
  IconFileDescription,
  IconInfoCircle,
  IconRefresh,
  IconRocket,
  IconSend,
  IconSparkles,
  IconTag,
  IconTemplate,
  IconUpload,
  IconUsers,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import type {
  HeaderInput,
  CarouselSendCardInput,
} from '../../../../api/src/modules/campaigns/schema';
import {
  CarouselSendEditor,
  detectCarouselCards,
} from '../../components/templates/CarouselSendEditor';
import {
  extractBodyText,
  extractButtons,
  extractHeaderFormat,
  extractId,
  hasComponent,
} from '../../lib/template-introspection';
import {
  TokenTextInput,
  buildContactTokenOptions,
} from '../../components/flow-builder/TokenTextInput';
import { TemplatePreview, type PreviewTemplate } from '../../components/TemplatePreview';

/**
 * Modern, standard 3-step campaign creation wizard:
 * 1. Audience & Details (Who)
 * 2. Message & Live Preview (What)
 * 3. Schedule, Pre-Flight Health & Launch (When & Review)
 */
function NewCampaign() {
  const navigate = useNavigate();
  const groups = trpc.contacts.groups.list.useQuery();
  const labels = trpc.contacts.labels.list.useQuery();
  const templates = trpc.whatsapp.templates.list.useQuery();
  const forms = trpc.forms.list.useQuery();
  const customFields = trpc.contacts.customFields.list.useQuery();

  const previewAudience = trpc.campaigns.previewAudience.useMutation();
  const preflight = trpc.campaigns.preflight.useMutation();

  const createAndSchedule = trpc.campaigns.createAndSchedule.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Campaign scheduled successfully — ${r.queued} recipients queued`,
        color: 'green',
      });
      navigate({ to: '/vendor/campaigns/$uid/status', params: { uid: r.uid } });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const saveDraft = trpc.campaigns.saveDraft.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Campaign draft saved', color: 'green' });
      navigate({ to: '/vendor/campaigns' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // Step state: 0 = Audience, 1 = Message, 2 = Schedule & Review
  const [step, setStep] = useState(0);

  // Campaign & Audience fields
  const [title, setTitle] = useState('');
  const [audienceMode, setAudienceMode] = useState<'all' | 'custom'>('all');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [restrictByLanguage, setRestrictByLanguage] = useState(false);

  // Message fields
  const [whatsAppTemplateId, setWhatsAppTemplateId] = useState<number | null>(null);
  const [flowUid, setFlowUid] = useState<string | null>(null);
  const [bodyVariables, setBodyVariables] = useState<string[]>([]);
  const [header, setHeader] = useState<HeaderInput | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [limitedTimeOfferExpiresAt, setLimitedTimeOfferExpiresAt] = useState<Date | null>(null);
  const [carousel, setCarousel] = useState<CarouselSendCardInput[]>([]);
  const [abVariants, setAbVariants] = useState<
    Array<{ key: string; whatsAppTemplateId: number | null; weight: number }>
  >([]);

  // Schedule fields
  const [sendTiming, setSendTiming] = useState<'immediate' | 'scheduled'>('immediate');
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [expiryAt, setExpiryAt] = useState<Date | null>(null);
  const [injectUtm, setInjectUtm] = useState(true);

  // Test send dialog
  const [sendTestOpen, setSendTestOpen] = useState(false);

  // Token options for variables
  const tokenOptions = useMemo(
    () => buildContactTokenOptions(customFields.data ?? []),
    [customFields.data],
  );

  // Selected template introspection
  const selectedTemplate = useMemo(() => {
    if (!whatsAppTemplateId) return null;
    const all = templates.data ?? [];
    return all.find((t) => extractId(t) === whatsAppTemplateId) ?? null;
  }, [whatsAppTemplateId, templates.data]);

  const detectedVars = useMemo(() => {
    const body =
      (selectedTemplate as { bodyText?: string } | null)?.bodyText ??
      extractBodyText(selectedTemplate);
    if (!body) return [] as number[];
    const out = new Set<number>();
    for (const m of body.matchAll(/\{\{(\d+)\}\}/g)) out.add(Number(m[1]));
    return [...out].sort((a, b) => a - b);
  }, [selectedTemplate]);

  useEffect(() => {
    setBodyVariables((prev) => {
      const next = prev.slice(0, detectedVars.length);
      while (next.length < detectedVars.length) next.push('');
      return next;
    });
  }, [detectedVars.length]);

  const headerFormat = useMemo(() => {
    return ((selectedTemplate as { headerFormat?: string } | null)?.headerFormat ??
      extractHeaderFormat(selectedTemplate) ??
      'NONE') as 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
  }, [selectedTemplate]);

  useEffect(() => {
    if (headerFormat === 'NONE') {
      setHeader(null);
    } else if (headerFormat === 'TEXT') {
      setHeader({ kind: 'text', variables: [] });
    } else if (headerFormat === 'IMAGE') {
      setHeader({ kind: 'image' });
    } else if (headerFormat === 'VIDEO') {
      setHeader({ kind: 'video' });
    } else if (headerFormat === 'DOCUMENT') {
      setHeader({ kind: 'document' });
    } else if (headerFormat === 'LOCATION') {
      setHeader({ kind: 'location', latitude: 0, longitude: 0 });
    }
  }, [headerFormat]);

  const showCouponCode = useMemo(() => hasCopyCodeButton(selectedTemplate), [selectedTemplate]);
  const showLimitedTimeOffer = useMemo(
    () => hasLimitedTimeOffer(selectedTemplate),
    [selectedTemplate],
  );
  const carouselCards = useMemo(() => detectCarouselCards(selectedTemplate), [selectedTemplate]);

  useEffect(() => {
    if (!showCouponCode) setCouponCode('');
  }, [showCouponCode]);
  useEffect(() => {
    if (!showLimitedTimeOffer) setLimitedTimeOfferExpiresAt(null);
  }, [showLimitedTimeOffer]);
  useEffect(() => {
    setCarousel([]);
  }, [carouselCards]);

  // Generate payload for preview, preflight, and schedule mutations
  const buildInput = () => ({
    title: title.trim() || 'Untitled campaign',
    channel: 'whatsapp' as const,
    whatsAppTemplateId,
    audience: {
      allContacts: audienceMode === 'all',
      groupIds: groupIds.map(Number),
      labelIds: labelIds.map(Number),
      contactIds: [] as number[],
    },
    bodyVariables: bodyVariables.length > 0 ? bodyVariables : undefined,
    header: header ?? undefined,
    couponCode: showCouponCode && couponCode.trim() ? couponCode.trim() : undefined,
    limitedTimeOfferExpiresAt:
      showLimitedTimeOffer && limitedTimeOfferExpiresAt
        ? limitedTimeOfferExpiresAt.toISOString()
        : undefined,
    carousel: carouselCards && carousel.length > 0 ? carousel : undefined,
    abVariants:
      abVariants.length >= 2
        ? abVariants
            .filter((v) => v.whatsAppTemplateId !== null)
            .map((v) => ({
              key: v.key,
              whatsAppTemplateId: v.whatsAppTemplateId!,
              weight: v.weight,
            }))
        : undefined,
    restrictByTemplateLanguage: restrictByLanguage,
    scheduledAt: sendTiming === 'scheduled' && scheduledAt ? scheduledAt.toISOString() : null,
    expiryAt: expiryAt ? expiryAt.toISOString() : null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    recurrenceJson: null,
    injectUtm,
    flowUid: flowUid ?? undefined,
  });

  // Auto-refresh audience estimation whenever audience parameters change
  useEffect(() => {
    const isReady = audienceMode === 'all' || groupIds.length > 0 || labelIds.length > 0;
    if (isReady) {
      const timer = setTimeout(() => {
        previewAudience.mutate(buildInput());
      }, 350);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceMode, groupIds, labelIds, restrictByLanguage, whatsAppTemplateId]);

  // Auto-run pre-flight checks whenever Step 3 (Review & Schedule) is opened
  useEffect(() => {
    if (step === 2) {
      preflight.mutate(buildInput());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, whatsAppTemplateId, sendTiming, scheduledAt]);

  // Real-time template preview object for the right-hand companion phone mockup
  const previewTemplate = useMemo<PreviewTemplate | null>(() => {
    if (!selectedTemplate) return null;
    const raw = selectedTemplate as {
      category?: string;
      components?: Array<{ type?: string; text?: string }>;
    };
    const bodyText =
      (selectedTemplate as { bodyText?: string } | null)?.bodyText ??
      extractBodyText(selectedTemplate) ??
      '';
    const footerText =
      raw.components?.find((c) => (c.type ?? '').toUpperCase() === 'FOOTER')?.text ?? undefined;
    const headerText =
      raw.components?.find((c) => (c.type ?? '').toUpperCase() === 'HEADER')?.text ?? undefined;

    const upper = (raw.category ?? '').toUpperCase();
    const category = (
      upper === 'UTILITY' || upper === 'AUTHENTICATION' ? upper : 'MARKETING'
    ) as PreviewTemplate['category'];

    const mediaPreviewUrl =
      header && 'link' in header && typeof header.link === 'string' ? header.link : undefined;

    const buttons = extractButtons(selectedTemplate).map((b) =>
      b.type === 'COPY_CODE' && couponCode.trim()
        ? { ...b, copyCodeExample: couponCode.trim() }
        : b,
    );

    return {
      category,
      header:
        headerFormat === 'NONE'
          ? undefined
          : { format: headerFormat, text: headerText, mediaPreviewUrl },
      bodyText,
      bodyExamples: bodyVariables,
      footerText,
      buttons,
      ...(showLimitedTimeOffer
        ? {
            limitedTimeOffer: {
              text: 'Limited time offer',
              hasExpiration: limitedTimeOfferExpiresAt != null,
            },
          }
        : {}),
      ...(carouselCards
        ? {
            carouselCards: carouselCards.map((c, i) => ({
              headerFormat: c.headerFormat,
              mediaPreviewUrl: null,
              bodyText: (carousel[i]?.bodyVariables ?? []).join(' '),
              buttons: c.buttonTypes.map((type) => ({ type })),
            })),
          }
        : {}),
    };
  }, [
    selectedTemplate,
    headerFormat,
    header,
    bodyVariables,
    couponCode,
    showLimitedTimeOffer,
    limitedTimeOfferExpiresAt,
    carouselCards,
    carousel,
  ]);

  // Validation checks for navigation
  const canProceedFromAudience =
    title.trim().length > 0 &&
    (audienceMode === 'all' || groupIds.length > 0 || labelIds.length > 0);

  const canProceedFromMessage = whatsAppTemplateId !== null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg" maw={1200} mx="auto" pb="xl">
        {/* Header Bar */}
        <Card withBorder radius="md" p="md" shadow="xs">
          <Group justify="space-between" align="center">
            <div>
              <Title order={3}>Create Campaign</Title>
              <Text size="sm" c="dimmed">
                Set up, customize, and launch your WhatsApp broadcast in 3 easy steps.
              </Text>
            </div>
            <Group>
              <Button
                variant="subtle"
                color="gray"
                onClick={() => saveDraft.mutate(buildInput())}
                loading={saveDraft.isPending}
              >
                Save as draft
              </Button>
              <Button
                variant="light"
                leftSection={<IconSend size={15} />}
                onClick={() => setSendTestOpen(true)}
                disabled={!whatsAppTemplateId}
              >
                Send test
              </Button>
            </Group>
          </Group>
        </Card>

        {/* 3-Step Wizard Stepper */}
        <Stepper
          active={step}
          onStepClick={setStep}
          styles={{
            stepBody: { display: 'flex', flexDirection: 'column' },
            stepLabel: { fontWeight: 600, fontSize: 14 },
            stepDescription: { fontSize: 12, color: 'gray' },
          }}
        >
          <Stepper.Step
            label="Audience"
            description="Who will receive this?"
            icon={<IconUsers size={16} />}
          />
          <Stepper.Step
            label="Message"
            description="Content & preview"
            icon={<IconTemplate size={16} />}
          />
          <Stepper.Step
            label="Review & Schedule"
            description="Pre-flight & launch"
            icon={<IconRocket size={16} />}
          />
        </Stepper>

        {/* STEP 1: AUDIENCE & DETAILS */}
        {step === 0 && (
          <Stack gap="md">
            <Card withBorder radius="md" p="lg" shadow="xs">
              <Stack gap="md">
                <div>
                  <Text fw={600} size="md">
                    Campaign Details
                  </Text>
                  <Text size="xs" c="dimmed">
                    Give your campaign a memorable internal name to track its performance.
                  </Text>
                </div>
                <TextInput
                  label="Campaign Name"
                  placeholder="e.g. Summer VIP Offer - 20% Off"
                  required
                  size="sm"
                  value={title}
                  onChange={(e) => setTitle(e.currentTarget.value)}
                  error={!title.trim() && 'Campaign name is required'}
                />
              </Stack>
            </Card>

            <Card withBorder radius="md" p="lg" shadow="xs">
              <Stack gap="md">
                <div>
                  <Text fw={600} size="md">
                    Target Audience
                  </Text>
                  <Text size="xs" c="dimmed">
                    Choose who should receive this campaign blast.
                  </Text>
                </div>

                <Radio.Group
                  value={audienceMode}
                  onChange={(val) => setAudienceMode(val as 'all' | 'custom')}
                >
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <Paper
                      withBorder
                      p="md"
                      radius="md"
                      style={{
                        cursor: 'pointer',
                        borderColor:
                          audienceMode === 'all' ? 'var(--mantine-color-blue-6)' : undefined,
                        backgroundColor:
                          audienceMode === 'all' ? 'var(--mantine-color-blue-0)' : undefined,
                      }}
                      onClick={() => setAudienceMode('all')}
                    >
                      <Radio
                        value="all"
                        label="All Eligible Contacts"
                        description="Send broadcast to everyone in your contact list with valid WhatsApp numbers."
                      />
                    </Paper>

                    <Paper
                      withBorder
                      p="md"
                      radius="md"
                      style={{
                        cursor: 'pointer',
                        borderColor:
                          audienceMode === 'custom' ? 'var(--mantine-color-blue-6)' : undefined,
                        backgroundColor:
                          audienceMode === 'custom' ? 'var(--mantine-color-blue-0)' : undefined,
                      }}
                      onClick={() => setAudienceMode('custom')}
                    >
                      <Radio
                        value="custom"
                        label="Segment by Groups & Tags"
                        description="Narrow your reach to specific customer segments or tag labels."
                      />
                    </Paper>
                  </SimpleGrid>
                </Radio.Group>

                {audienceMode === 'custom' && (
                  <Stack gap="sm" pt="xs">
                    <MultiSelect
                      label="Contact Groups"
                      placeholder="Select one or more groups..."
                      leftSection={<IconUsers size={16} />}
                      data={(groups.data ?? []).map((g) => ({
                        value: String(g.id),
                        label: g.title,
                      }))}
                      value={groupIds}
                      onChange={setGroupIds}
                      searchable
                      clearable
                    />

                    <MultiSelect
                      label="Contact Tags & Labels"
                      placeholder="Select one or more tags..."
                      leftSection={<IconTag size={16} />}
                      data={(labels.data ?? []).map((l) => ({
                        value: String(l.id),
                        label: l.title,
                      }))}
                      value={labelIds}
                      onChange={setLabelIds}
                      searchable
                      clearable
                    />

                    <Text size="xs" c="dimmed">
                      Contacts in <strong>any</strong> of the selected groups or tags will be
                      included. Duplicate contacts are automatically sent only once.
                    </Text>
                  </Stack>
                )}

                <Divider my="xs" />

                <Switch
                  label="Match template language preference"
                  description="Only send to contacts whose preferred language matches the selected template (recommended for multilingual brands)."
                  checked={restrictByLanguage}
                  onChange={(e) => setRestrictByLanguage(e.currentTarget.checked)}
                />
              </Stack>
            </Card>

            {/* Dynamic Audience Counter */}
            <Card withBorder radius="md" p="md" bg="gray.0">
              <Group justify="space-between" align="center">
                <Group gap="md">
                  <ThemeIcon size={42} radius="md" color="blue" variant="light">
                    <IconUsers size={22} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed" fw={500} tt="uppercase">
                      Estimated Audience Reach
                    </Text>
                    {previewAudience.isPending ? (
                      <Skeleton height={24} width={120} mt={4} />
                    ) : previewAudience.data ? (
                      <Group gap="xs" align="baseline">
                        <Text size="xl" fw={700} c="blue.8">
                          {previewAudience.data.eligible.toLocaleString()} contacts
                        </Text>
                        {previewAudience.data.suppressed > 0 && (
                          <Badge color="orange" variant="light" size="sm">
                            {previewAudience.data.suppressed.toLocaleString()} suppressed /
                            opted-out
                          </Badge>
                        )}
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">
                        {audienceMode === 'custom' && groupIds.length === 0 && labelIds.length === 0
                          ? 'Select at least one group or tag above to calculate reach'
                          : 'Calculating audience size...'}
                      </Text>
                    )}
                  </div>
                </Group>

                <Tooltip label="Recalculate reach" withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    loading={previewAudience.isPending}
                    onClick={() => previewAudience.mutate(buildInput())}
                  >
                    <IconRefresh size={18} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Card>

            {/* Step Navigation Bar */}
            <Group justify="flex-end" mt="md">
              <Button
                size="md"
                rightSection={<IconArrowRight size={16} />}
                disabled={!canProceedFromAudience}
                onClick={() => setStep(1)}
              >
                Continue to Message
              </Button>
            </Group>
          </Stack>
        )}

        {/* STEP 2: MESSAGE & LIVE PREVIEW */}
        {step === 1 && (
          <Grid gap="lg">
            {/* Left Column: Form & Content Configuration */}
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack gap="md">
                <Card withBorder radius="md" p="lg" shadow="xs">
                  <Stack gap="md">
                    <div>
                      <Text fw={600} size="md">
                        Choose WhatsApp Template
                      </Text>
                      <Text size="xs" c="dimmed">
                        Select a Meta-approved message template for your outreach.
                      </Text>
                    </div>

                    {templates.isPending ? (
                      <Skeleton height={42} />
                    ) : (
                      <Select
                        label="Template"
                        placeholder="Search approved templates..."
                        leftSection={<IconTemplate size={16} />}
                        data={(templates.data ?? [])
                          .filter((t) => t.status === 'APPROVED')
                          .map((t) => ({
                            value: String(extractId(t)),
                            label: `${t.name} (${t.language})`,
                          }))}
                        value={whatsAppTemplateId ? String(whatsAppTemplateId) : null}
                        onChange={(v) => setWhatsAppTemplateId(v ? Number(v) : null)}
                        searchable
                        clearable
                      />
                    )}

                    {selectedTemplate && (
                      <Group gap="xs">
                        <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                          Meta Approved
                        </Badge>
                        <Badge color="blue" variant="light">
                          Language: {(selectedTemplate as { language?: string }).language ?? 'en'}
                        </Badge>
                        {(selectedTemplate as { category?: string }).category && (
                          <Badge color="gray" variant="outline">
                            {(selectedTemplate as { category?: string }).category}
                          </Badge>
                        )}
                      </Group>
                    )}
                  </Stack>
                </Card>

                {/* Body Variables Personalization */}
                {selectedTemplate && detectedVars.length > 0 && (
                  <Card withBorder radius="md" p="lg" shadow="xs">
                    <Stack gap="sm">
                      <div>
                        <Text fw={600} size="md">
                          Personalize Message Placeholders
                        </Text>
                        <Text size="xs" c="dimmed">
                          Fill in the variables in your template. Insert dynamic contact tokens or
                          type static text.
                        </Text>
                      </div>

                      {detectedVars.map((n, i) => (
                        <TokenTextInput
                          key={n}
                          label={`Placeholder {{${n}}}`}
                          options={tokenOptions}
                          value={bodyVariables[i] ?? ''}
                          onChange={(value) =>
                            setBodyVariables((prev) => {
                              const next = [...prev];
                              next[i] = value;
                              return next;
                            })
                          }
                        />
                      ))}
                    </Stack>
                  </Card>
                )}

                {/* Header Media Config */}
                {selectedTemplate && headerFormat !== 'NONE' && (
                  <Card withBorder radius="md" p="lg" shadow="xs">
                    <CampaignHeaderConfig
                      format={headerFormat}
                      header={header}
                      onChange={setHeader}
                    />
                  </Card>
                )}

                {/* Progressive Disclosure: Advanced Settings Accordion */}
                <Accordion variant="separated" radius="md">
                  {/* A/B Testing */}
                  <Accordion.Item value="ab_testing">
                    <Accordion.Control icon={<IconSparkles size={16} />}>
                      <Group justify="space-between" pr="sm">
                        <Text size="sm" fw={500}>
                          A/B Variant Testing (Optional)
                        </Text>
                        {abVariants.length >= 2 && (
                          <Badge color="violet" size="sm">
                            {abVariants.length} Variants Active
                          </Badge>
                        )}
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <AbVariantsEditor
                        variants={abVariants}
                        onChange={setAbVariants}
                        templates={templates.data ?? []}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>

                  {/* Attach WhatsApp Flow Form */}
                  <Accordion.Item value="form_attachment">
                    <Accordion.Control icon={<IconFileDescription size={16} />}>
                      <Text size="sm" fw={500}>
                        Interactive WhatsApp Flow Form (Optional)
                      </Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Select
                        label="Attach Published Form"
                        description="Only used when the chosen template contains a WhatsApp Flow button."
                        placeholder="Choose a form..."
                        clearable
                        searchable
                        data={(forms.data ?? [])
                          .filter((fm) => fm.status === 'PUBLISHED')
                          .map((fm) => ({ value: fm.uid, label: fm.name }))}
                        value={flowUid}
                        onChange={setFlowUid}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>

                  {/* Offer Code & Timer (Only shown if template supports it) */}
                  {(showCouponCode || showLimitedTimeOffer) && (
                    <Accordion.Item value="offers">
                      <Accordion.Control icon={<IconClock size={16} />}>
                        <Text size="sm" fw={500}>
                          Promotional Coupon & Countdown
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="sm">
                          {showCouponCode && (
                            <TextInput
                              label="Coupon / Promo Code"
                              description="Real code delivered to every recipient for the COPY_CODE button."
                              maxLength={20}
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.currentTarget.value)}
                            />
                          )}
                          {showLimitedTimeOffer && (
                            <DateTimePicker
                              label="Offer Expiration Countdown"
                              description="Countdown deadline shown on the offer banner."
                              clearable
                              minDate={new Date()}
                              value={limitedTimeOfferExpiresAt}
                              onChange={(v) => setLimitedTimeOfferExpiresAt(v ? new Date(v) : null)}
                            />
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {/* Carousel Cards (Only if carousel template) */}
                  {carouselCards && (
                    <Accordion.Item value="carousel">
                      <Accordion.Control icon={<IconTemplate size={16} />}>
                        <Text size="sm" fw={500}>
                          Carousel Cards Configuration
                        </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <CarouselSendEditor cards={carouselCards} onChange={setCarousel} />
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}
                </Accordion>

                {/* Step Navigation Bar */}
                <Group justify="space-between" mt="md">
                  <Button
                    variant="default"
                    size="md"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => setStep(0)}
                  >
                    Back to Audience
                  </Button>
                  <Button
                    size="md"
                    rightSection={<IconArrowRight size={16} />}
                    disabled={!canProceedFromMessage}
                    onClick={() => setStep(2)}
                  >
                    Continue to Schedule & Review
                  </Button>
                </Group>
              </Stack>
            </Grid.Col>

            {/* Right Column: Live Companion WhatsApp Mockup Preview */}
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Box style={{ position: 'sticky', top: 20 }}>
                <Card withBorder radius="md" p="md" shadow="sm">
                  <Stack gap="sm">
                    <Group justify="space-between" align="center">
                      <Group gap="xs">
                        <IconDeviceMobile size={18} color="green" />
                        <Text fw={600} size="sm">
                          Live Message Preview
                        </Text>
                      </Group>
                      <Badge color="teal" variant="light" size="sm">
                        WhatsApp
                      </Badge>
                    </Group>

                    {previewTemplate ? (
                      <Stack gap="xs">
                        <CampaignPreview template={previewTemplate} />

                        <Paper withBorder p="sm" radius="md" bg="blue.0" mt="xs">
                          <Group justify="space-between" align="center">
                            <div>
                              <Text size="xs" fw={600} c="blue.9">
                                Test on your own phone
                              </Text>
                              <Text size="xs" c="dimmed">
                                See how this looks on WhatsApp right now.
                              </Text>
                            </div>
                            <Button
                              size="xs"
                              variant="light"
                              color="blue"
                              leftSection={<IconSend size={12} />}
                              onClick={() => setSendTestOpen(true)}
                            >
                              Send test
                            </Button>
                          </Group>
                        </Paper>
                      </Stack>
                    ) : (
                      <Box
                        p="xl"
                        style={{
                          textAlign: 'center',
                          borderRadius: 8,
                          backgroundColor: '#f8f9fa',
                          minHeight: 340,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ThemeIcon size={48} radius="xl" color="gray" variant="light" mb="sm">
                          <IconTemplate size={24} />
                        </ThemeIcon>
                        <Text size="sm" fw={500} c="dimmed">
                          Select a template on the left to preview your message here in real-time.
                        </Text>
                      </Box>
                    )}
                  </Stack>
                </Card>
              </Box>
            </Grid.Col>
          </Grid>
        )}

        {/* STEP 3: SCHEDULE, HEALTH CHECK & REVIEW */}
        {step === 2 && (
          <Stack gap="lg">
            {/* Delivery Timing Options */}
            <Card withBorder radius="md" p="lg" shadow="xs">
              <Stack gap="md">
                <div>
                  <Text fw={600} size="md">
                    Delivery Schedule
                  </Text>
                  <Text size="xs" c="dimmed">
                    Decide when this campaign should start sending.
                  </Text>
                </div>

                <Radio.Group
                  value={sendTiming}
                  onChange={(val) => setSendTiming(val as 'immediate' | 'scheduled')}
                >
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <Paper
                      withBorder
                      p="md"
                      radius="md"
                      style={{
                        cursor: 'pointer',
                        borderColor:
                          sendTiming === 'immediate' ? 'var(--mantine-color-blue-6)' : undefined,
                        backgroundColor:
                          sendTiming === 'immediate' ? 'var(--mantine-color-blue-0)' : undefined,
                      }}
                      onClick={() => setSendTiming('immediate')}
                    >
                      <Radio
                        value="immediate"
                        label="Send Immediately"
                        description="Start sending to queued recipients right away upon launch."
                      />
                    </Paper>

                    <Paper
                      withBorder
                      p="md"
                      radius="md"
                      style={{
                        cursor: 'pointer',
                        borderColor:
                          sendTiming === 'scheduled' ? 'var(--mantine-color-blue-6)' : undefined,
                        backgroundColor:
                          sendTiming === 'scheduled' ? 'var(--mantine-color-blue-0)' : undefined,
                      }}
                      onClick={() => setSendTiming('scheduled')}
                    >
                      <Radio
                        value="scheduled"
                        label="Schedule for Later"
                        description="Pick a future date and time for automated dispatch."
                      />
                    </Paper>
                  </SimpleGrid>
                </Radio.Group>

                {sendTiming === 'scheduled' && (
                  <Group grow align="start" pt="xs">
                    <DateTimePicker
                      label="Scheduled Send Date & Time"
                      placeholder="Select delivery time"
                      minDate={new Date()}
                      value={scheduledAt}
                      onChange={(v) => setScheduledAt(v ? new Date(v) : null)}
                      clearable
                      required
                    />
                    <Box pt={24}>
                      <Text size="xs" c="dimmed">
                        Timezone:{' '}
                        <strong>{Intl.DateTimeFormat().resolvedOptions().timeZone}</strong>
                      </Text>
                    </Box>
                  </Group>
                )}

                <Divider my="xs" />

                {/* Additional Delivery Settings */}
                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm" fw={500}>
                      Link Tracking (Google Analytics)
                    </Text>
                    <Text size="xs" c="dimmed">
                      Automatically add UTM campaign parameters to button links to track sales and
                      traffic.
                    </Text>
                  </div>
                  <Switch
                    checked={injectUtm}
                    onChange={(e) => setInjectUtm(e.currentTarget.checked)}
                  />
                </Group>

                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm" fw={500}>
                      Delivery Expiration (Optional)
                    </Text>
                    <Text size="xs" c="dimmed">
                      Stop sending if any messages remain in queue past this time.
                    </Text>
                  </div>
                  <DateTimePicker
                    placeholder="No expiration"
                    value={expiryAt}
                    onChange={(v) => setExpiryAt(v ? new Date(v) : null)}
                    clearable
                    w={240}
                  />
                </Group>
              </Stack>
            </Card>

            {/* Automated Health & Pre-Flight Verification */}
            <Card withBorder radius="md" p="lg" shadow="xs">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <div>
                    <Group gap="xs">
                      <Text fw={600} size="md">
                        Pre-Launch Health Check
                      </Text>
                      {preflight.isPending ? (
                        <Badge color="gray" variant="light">
                          Checking...
                        </Badge>
                      ) : preflight.data?.whatsappCanSend !== false ? (
                        <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                          All Systems Ready
                        </Badge>
                      ) : (
                        <Badge
                          color="red"
                          variant="light"
                          leftSection={<IconAlertCircle size={12} />}
                        >
                          Action Required
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      Automated safety verification of your WhatsApp account, template status, and
                      target audience.
                    </Text>
                  </div>
                  <Button
                    variant="subtle"
                    size="xs"
                    leftSection={<IconRefresh size={14} />}
                    loading={preflight.isPending}
                    onClick={() => preflight.mutate(buildInput())}
                  >
                    Re-check
                  </Button>
                </Group>

                {preflight.isPending ? (
                  <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
                    <Skeleton height={60} radius="md" />
                    <Skeleton height={60} radius="md" />
                    <Skeleton height={60} radius="md" />
                    <Skeleton height={60} radius="md" />
                  </SimpleGrid>
                ) : preflight.data ? (
                  <Stack gap="sm">
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
                      {/* Check 1: Account Status */}
                      <Paper withBorder p="sm" radius="md">
                        <Group justify="space-between">
                          <Text size="xs" c="dimmed" fw={500}>
                            Account Status
                          </Text>
                          {preflight.data.whatsappConfigured ? (
                            <IconCircleCheck size={18} color="green" />
                          ) : (
                            <IconAlertCircle size={18} color="red" />
                          )}
                        </Group>
                        <Text size="sm" fw={600} mt={4}>
                          {preflight.data.whatsappConfigured ? 'Connected' : 'Not Configured'}
                        </Text>
                      </Paper>

                      {/* Check 2: Template Approval */}
                      <Paper withBorder p="sm" radius="md">
                        <Group justify="space-between">
                          <Text size="xs" c="dimmed" fw={500}>
                            Template Status
                          </Text>
                          {preflight.data.templateApproved ? (
                            <IconCircleCheck size={18} color="green" />
                          ) : (
                            <IconAlertCircle size={18} color="red" />
                          )}
                        </Group>
                        <Text size="sm" fw={600} mt={4}>
                          {preflight.data.templateApproved ? 'Meta Approved' : 'Pending Approval'}
                        </Text>
                      </Paper>

                      {/* Check 3: Phone Quality */}
                      <Paper withBorder p="sm" radius="md">
                        <Group justify="space-between">
                          <Text size="xs" c="dimmed" fw={500}>
                            Phone Quality
                          </Text>
                          <Badge
                            size="sm"
                            color={
                              preflight.data.phoneQuality === 'GREEN'
                                ? 'green'
                                : preflight.data.phoneQuality === 'YELLOW'
                                  ? 'yellow'
                                  : 'red'
                            }
                          >
                            {preflight.data.phoneQuality ?? 'High'}
                          </Badge>
                        </Group>
                        <Text size="sm" fw={600} mt={4}>
                          Tier: {preflight.data.messagingTier ?? 'Standard'}
                        </Text>
                      </Paper>

                      {/* Check 4: Verified Reach */}
                      <Paper withBorder p="sm" radius="md">
                        <Group justify="space-between">
                          <Text size="xs" c="dimmed" fw={500}>
                            Recipients
                          </Text>
                          <IconCircleCheck size={18} color="green" />
                        </Group>
                        <Text size="sm" fw={600} mt={4}>
                          {preflight.data.audience.eligible.toLocaleString()} Ready
                        </Text>
                      </Paper>
                    </SimpleGrid>

                    {preflight.data.whatsappCanSend === false && (
                      <Alert color="red" icon={<IconAlertCircle />}>
                        Meta has restricted messaging on this WABA. Check your Meta Business Manager
                        settings before launching.
                      </Alert>
                    )}

                    {preflight.data.warnings && preflight.data.warnings.length > 0 && (
                      <Alert color="yellow" icon={<IconAlertTriangle />}>
                        <Stack gap={2}>
                          {preflight.data.warnings.map((w) => (
                            <Text key={w} size="xs">
                              • {w}
                            </Text>
                          ))}
                        </Stack>
                      </Alert>
                    )}
                  </Stack>
                ) : null}
              </Stack>
            </Card>

            {/* Campaign Summary Card */}
            <Card withBorder radius="md" p="lg" shadow="xs">
              <Stack gap="sm">
                <Text fw={600} size="md">
                  Campaign Summary
                </Text>

                <Table withTableBorder={false} withRowBorders={false}>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td w={180} c="dimmed">
                        Campaign Title
                      </Table.Td>
                      <Table.Td fw={600}>{title || 'Untitled campaign'}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td c="dimmed">Audience</Table.Td>
                      <Table.Td>
                        {audienceMode === 'all'
                          ? 'All eligible contacts'
                          : `${groupIds.length} group(s), ${labelIds.length} tag(s)`}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td c="dimmed">Template</Table.Td>
                      <Table.Td>
                        {selectedTemplate
                          ? `${(selectedTemplate as { name?: string }).name ?? 'Template'} (${(selectedTemplate as { language?: string }).language ?? 'en'})`
                          : '—'}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td c="dimmed">Send Schedule</Table.Td>
                      <Table.Td>
                        {sendTiming === 'immediate'
                          ? 'Immediately upon launch'
                          : scheduledAt
                            ? scheduledAt.toLocaleString()
                            : 'Scheduled'}
                      </Table.Td>
                    </Table.Tr>
                    {bodyVariables.length > 0 && (
                      <Table.Tr>
                        <Table.Td c="dimmed">Variables</Table.Td>
                        <Table.Td>{bodyVariables.join(' | ')}</Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Card>

            {/* Step Navigation Bar & Primary Launch Action */}
            <Group justify="space-between" mt="md">
              <Button
                variant="default"
                size="md"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => setStep(1)}
              >
                Back to Message
              </Button>

              <Group>
                <Button
                  variant="subtle"
                  size="md"
                  color="gray"
                  onClick={() => saveDraft.mutate(buildInput())}
                  loading={saveDraft.isPending}
                >
                  Save as draft
                </Button>
                <Button
                  size="md"
                  color="blue"
                  leftSection={<IconRocket size={18} />}
                  loading={createAndSchedule.isPending}
                  onClick={() => createAndSchedule.mutate(buildInput())}
                >
                  {sendTiming === 'immediate' ? 'Launch Campaign' : 'Schedule Campaign'}
                </Button>
              </Group>
            </Group>
          </Stack>
        )}
      </Stack>

      {/* Test Message Send Modal */}
      <SendTestDialog
        opened={sendTestOpen}
        onClose={() => setSendTestOpen(false)}
        templateId={whatsAppTemplateId}
        bodyVariables={bodyVariables}
      />
    </motion.div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function hasCopyCodeButton(t: unknown): boolean {
  return extractButtons(t).some((b) => b.type === 'COPY_CODE');
}

function hasLimitedTimeOffer(t: unknown): boolean {
  return hasComponent(t, 'LIMITED_TIME_OFFER');
}

// ── Header configurator ──────────────────────────────────────────────
function CampaignHeaderConfig({
  format,
  header,
  onChange,
}: {
  format: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
  header: HeaderInput | null;
  onChange: (h: HeaderInput | null) => void;
}) {
  const upload = trpc.whatsapp.templates.uploadSendMedia.useMutation();

  const handleFile = async (file: File | null) => {
    if (!file) return;
    const buf = await file.arrayBuffer();
    const base64 = bufferToBase64(buf);
    upload.mutate(
      { base64, mimeType: file.type, filename: file.name },
      {
        onSuccess: (r) => {
          if (format === 'IMAGE') onChange({ kind: 'image', mediaId: r.mediaId });
          else if (format === 'VIDEO') onChange({ kind: 'video', mediaId: r.mediaId });
          else if (format === 'DOCUMENT')
            onChange({ kind: 'document', mediaId: r.mediaId, filename: file.name });
          notifications.show({ message: 'Media attached to campaign', color: 'green' });
        },
        onError: (err) => notifications.show({ message: err.message, color: 'red' }),
      },
    );
  };

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        Header Media ({format})
      </Text>
      <Text size="xs" c="dimmed">
        This template requires a {format.toLowerCase()} header component.
      </Text>
      {format === 'TEXT' && (
        <TextInput
          label="Header text variable"
          placeholder="Sample value for the {{1}} in the header"
          value={header?.kind === 'text' ? (header.variables[0] ?? '') : ''}
          onChange={(e) => onChange({ kind: 'text', variables: [e.currentTarget.value] })}
        />
      )}
      {(format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT') && (
        <Group>
          <FileButton
            onChange={handleFile}
            accept={
              format === 'IMAGE' ? 'image/*' : format === 'VIDEO' ? 'video/*' : 'application/pdf'
            }
          >
            {(props) => (
              <Button
                {...props}
                variant="light"
                leftSection={<IconUpload size={14} />}
                loading={upload.isPending}
              >
                Upload {format.toLowerCase()}
              </Button>
            )}
          </FileButton>
          {header &&
            (header.kind === 'image' || header.kind === 'video' || header.kind === 'document') &&
            header.mediaId && (
              <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                Media Attached ✓
              </Badge>
            )}
        </Group>
      )}
      {format === 'LOCATION' && (
        <Group grow>
          <TextInput
            label="Latitude"
            value={header?.kind === 'location' ? String(header.latitude) : ''}
            onChange={(e) =>
              onChange({
                kind: 'location',
                latitude: Number(e.currentTarget.value) || 0,
                longitude: header?.kind === 'location' ? header.longitude : 0,
                ...(header?.kind === 'location'
                  ? { name: header.name, address: header.address }
                  : {}),
              })
            }
          />
          <TextInput
            label="Longitude"
            value={header?.kind === 'location' ? String(header.longitude) : ''}
            onChange={(e) =>
              onChange({
                kind: 'location',
                latitude: header?.kind === 'location' ? header.latitude : 0,
                longitude: Number(e.currentTarget.value) || 0,
                ...(header?.kind === 'location'
                  ? { name: header.name, address: header.address }
                  : {}),
              })
            }
          />
        </Group>
      )}
    </Stack>
  );
}

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

// ── Send-test dialog ─────────────────────────────────────────────────
function SendTestDialog({
  opened,
  onClose,
  templateId,
  bodyVariables,
}: {
  opened: boolean;
  onClose: () => void;
  templateId: number | null;
  bodyVariables: string[];
}) {
  const [phone, setPhone] = useState('');
  const sendTest = trpc.campaigns.sendTest.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Test message sent successfully', color: 'green' });
      onClose();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const placeholderValues: Record<string, string> = {};
  bodyVariables.forEach((v, i) => {
    placeholderValues[String(i + 1)] = v;
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Send Test WhatsApp Message"
      centered
      radius="md"
    >
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Enter your WhatsApp phone number with country code to preview the real message.
        </Text>
        <TextInput
          label="Phone Number (E.164 format)"
          placeholder="+919876543210"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!templateId || !phone.trim()}
            loading={sendTest.isPending}
            leftSection={<IconSend size={14} />}
            onClick={() =>
              templateId && sendTest.mutate({ templateId, toPhone: phone, placeholderValues })
            }
          >
            Send Test
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── A/B variants editor ─────────────────────────────────────────────
function AbVariantsEditor({
  variants,
  onChange,
  templates,
}: {
  variants: Array<{ key: string; whatsAppTemplateId: number | null; weight: number }>;
  onChange: (v: Array<{ key: string; whatsAppTemplateId: number | null; weight: number }>) => void;
  templates: Array<{
    id?: number | string | null;
    name?: string;
    language?: string;
    status?: string;
  }>;
}) {
  const approved = templates
    .filter((t) => t.status === 'APPROVED')
    .map((t) => ({
      value: String(extractId(t)),
      label: `${t.name} (${t.language})`,
    }));

  const addVariant = () => {
    if (variants.length >= 4) return;
    const nextKey = String.fromCharCode(65 + variants.length); // A, B, C, D
    onChange([...variants, { key: nextKey, whatsAppTemplateId: null, weight: 50 }]);
  };

  const updateVariant = (
    i: number,
    patch: Partial<{ whatsAppTemplateId: number | null; weight: number }>,
  ) => {
    onChange(variants.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  };

  const removeVariant = (i: number) => {
    onChange(variants.filter((_, idx) => idx !== i));
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          Test up to 4 templates side-by-side. Recipients are evenly distributed and analytics split
          per-variant.
        </Text>
        <Button size="xs" variant="light" onClick={addVariant} disabled={variants.length >= 4}>
          {variants.length === 0 ? 'Enable A/B testing' : '+ Add Variant'}
        </Button>
      </Group>
      {variants.length === 1 && (
        <Alert color="orange" variant="light" icon={<IconInfoCircle size={16} />}>
          A/B testing requires at least 2 variants. Add another, or remove this one.
        </Alert>
      )}
      {variants.map((v, i) => (
        <Group key={i} align="end" gap="xs">
          <Badge variant="light" size="lg" color="violet">
            {v.key}
          </Badge>
          <Select
            label={i === 0 ? 'Variant Template' : undefined}
            data={approved}
            value={v.whatsAppTemplateId ? String(v.whatsAppTemplateId) : null}
            onChange={(val) =>
              updateVariant(i, {
                whatsAppTemplateId: val ? Number(val) : null,
              })
            }
            style={{ flex: 1 }}
          />
          <TextInput
            label={i === 0 ? 'Weight %' : undefined}
            type="number"
            min={1}
            max={100}
            value={String(v.weight)}
            onChange={(e) =>
              updateVariant(i, {
                weight: Math.min(100, Math.max(1, Number(e.currentTarget.value) || 50)),
              })
            }
            w={85}
          />
          <Button color="red" variant="subtle" size="sm" onClick={() => removeVariant(i)}>
            Remove
          </Button>
        </Group>
      ))}
    </Stack>
  );
}

function CampaignPreview({ template }: { template: PreviewTemplate }) {
  const hasDynamicTokens = (template.bodyExamples ?? []).some((v) => /\{\{[a-z_.]+\}\}/i.test(v));
  return (
    <Stack gap={6}>
      <TemplatePreview template={template} />
      {hasDynamicTokens && (
        <Text size="xs" c="dimmed" ta="center">
          Tokens like {'{{first_name}}'} are replaced with each recipient&apos;s data at send time.
        </Text>
      )}
    </Stack>
  );
}

export const Route = createFileRoute('/vendor/campaigns/new')({ component: NewCampaign });
